import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link2, RefreshCw } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import CodeCommitRow from '../git/CodeCommitRow';
import PickLinkModal from '../git/PickLinkModal';
import CommitTaskChips, { useKnownTaskPrefixes } from '../git/CommitTaskChips';
import { codeRepoErrorKind, loadCodeRepoLog } from '../../services/git/codeRepoCommits';
import { localPathOf } from '../../services/git/codeRepoClones';
import { linkCommitToTask, tasksOfBoards } from '../../services/git/linkCommitToTask';
import { taskKeysFor } from '../../extensions/bundled/task-manager/taskNumbers';
import { useNotificationStore } from '../../store/notificationStore';
import { useAuthStore } from '../../store/authStore';
import { isTauri } from '../../utils/isTauri';
import '../boards/TaskCommits.css';

export const PAGE = 20;

/** "WEB-4 Login form — Web shop": what the task picker searches. */
export const taskPickLabel = (o) => `${o.key ? `${o.key} ` : ''}${o.title || ''} — ${o.boardName}`;

function RepoCommits({ repo, prefixes, onLinkCommit, nonce }) {
  const { t } = useTranslation();
  const path = localPathOf(repo);
  const [state, setState] = useState(path ? { status: 'loading' } : { status: 'noClone' });
  const [shown, setShown] = useState(PAGE);

  useEffect(() => {
    if (!path) return undefined;
    let alive = true;
    setState({ status: 'loading' });
    loadCodeRepoLog(path, { refresh: nonce > 0 })
      .then((log) => alive && setState({ status: 'ready', log }))
      .catch((e) => alive && setState({ status: 'error', kind: codeRepoErrorKind(e), error: String(e?.message || e) }));
    return () => {
      alive = false;
    };
  }, [path, nonce]);

  const commits = useMemo(
    () => (state.status === 'ready' ? state.log.commits.map((c) => ({ ...c, repo, repoId: repo.id, remoteUrl: repo.remote || state.log.remoteUrl || '' })) : []),
    [state, repo]
  );

  return (
    <div className="pj-code-repo" data-testid="code-repo">
      <div className="pj-code-repo-head">
        <span className="pj-code-repo-name">{repo.name}</span>
        {state.status === 'ready' && state.log.defaultBranch && <span className="task-commit-branch task-commit-branch--default">{state.log.defaultBranch}</span>}
      </div>
      {state.status === 'noClone' && <p className="task-commits-note">{t('taskLinks.code.notOnDevice', { name: repo.name })}</p>}
      {state.status === 'loading' && <p className="task-commits-note" role="status">{t('taskLinks.commits.loading')}</p>}
      {state.status === 'error' && (
        <p className="task-commits-note" role="alert">
          {state.kind === 'missing'
            ? t('taskLinks.commits.missing', { name: repo.name })
            : state.kind === 'notGit'
              ? t('taskLinks.commits.notGit', { name: repo.name })
              : t('taskLinks.commits.error', { name: repo.name, error: state.error })}
        </p>
      )}
      {state.status === 'ready' && commits.length === 0 && <p className="task-commits-note">{t('taskLinks.code.noCommits')}</p>}
      {commits.length > 0 && (
        <ul className="task-commits-list">
          {commits.slice(0, shown).map((c) => (
            <CodeCommitRow
              key={c.oid}
              commit={c}
              showRepo={false}
              actions={
                onLinkCommit && (
                  <Tooltip content={t('taskLinks.code.linkToTask')} side="top">
                    <button type="button" className="task-commit-open" onClick={() => onLinkCommit(c)} aria-label={t('taskLinks.code.linkToTask')} data-testid="code-link-task">
                      <Link2 size={13} aria-hidden />
                    </button>
                  </Tooltip>
                )
              }
            >
              <CommitTaskChips text={`${c.summary}\n${c.body || ''}\n${c.branch || ''}`} prefixes={prefixes} />
            </CodeCommitRow>
          ))}
        </ul>
      )}
      {commits.length > shown && (
        <button type="button" className="task-commits-hidden-toggle" onClick={() => setShown((n) => n + PAGE)}>
          {t('taskLinks.code.showMore', { count: Math.min(PAGE, commits.length - shown) })}
        </button>
      )}
    </div>
  );
}

/**
 * The project page's "Code" section (commit ↔ task links, slice 2): the recent
 * commits of each linked repository, read from its clone on this device, with
 * chips for the task keys they name and "Link to task…" (a task of the
 * project's boards). Desktop only — the web build has no local git.
 */
export default function ProjectCodeSection({ repos = [], members = [], readOnly = false }) {
  const { t } = useTranslation();
  const desktop = isTauri();
  const prefixes = useKnownTaskPrefixes();
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const [nonce, setNonce] = useState(0);
  const [linking, setLinking] = useState(null); // the commit being linked
  const [tasks, setTasks] = useState(null);
  const boardIds = useMemo(() => members.filter((m) => m.type === 'board').map((m) => String(m.id).replace(/^board:/, '')), [members]);
  const boardSig = boardIds.join('|');

  const openLink = useCallback(
    (commit) => {
      setLinking(commit);
      setTasks(null);
      tasksOfBoards(boardSig ? boardSig.split('|') : [], { keysFor: (b) => taskKeysFor(b) })
        .then(setTasks)
        .catch(() => setTasks([]));
    },
    [boardSig]
  );

  if (!repos.length) return null;
  return (
    <section className="pj-group pj-code" data-testid="project-code">
      <h3 className="pj-group-head pj-code-head">
        {t('taskLinks.code.title')}
        {desktop && (
          <Tooltip content={t('taskLinks.commits.refresh')} side="bottom">
            <button type="button" className="task-commits-refresh" onClick={() => setNonce((n) => n + 1)} aria-label={t('taskLinks.commits.refresh')}>
              <RefreshCw size={12} aria-hidden />
            </button>
          </Tooltip>
        )}
      </h3>
      {!desktop ? (
        <p className="task-commits-note">{t('taskLinks.commits.desktopOnly')}</p>
      ) : (
        repos.map((r) => (
          <RepoCommits key={r.id} repo={r} prefixes={prefixes} nonce={nonce} onLinkCommit={readOnly || !boardIds.length ? null : openLink} />
        ))
      )}
      {linking && (
        <PickLinkModal
          title={t('taskLinks.code.linkTitle', { sha: String(linking.oid).slice(0, 7) })}
          options={tasks || []}
          loading={tasks == null}
          getKey={(o) => `${o.boardId}:${o.taskId}`}
          getLabel={taskPickLabel}
          placeholder={t('taskLinks.code.pickPlaceholder')}
          searchPlaceholder={t('taskLinks.code.pickSearch')}
          emptyText={t('taskLinks.code.pickEmpty')}
          noMatchText={t('taskLinks.commits.pickNoMatch')}
          submitLabel={t('taskLinks.commits.linkSubmit')}
          onPick={async (o) => {
            try {
              const ok = await linkCommitToTask({ boardId: o.boardId, taskId: o.taskId, commit: linking, userId });
              if (ok) useNotificationStore.getState().success(t('taskLinks.code.linked', { key: o.key || o.title }), t('taskLinks.code.linkedTitle'));
              return ok;
            } catch (e) {
              useNotificationStore.getState().error(String(e?.message || e), t('taskLinks.code.linkFailed'));
              return false;
            }
          }}
          onClose={() => setLinking(null)}
        />
      )}
    </section>
  );
}
